import { ReferralLeaderboardEntry, ReferralSummary } from './referral.model';
import { maskReferralEmail } from '../../utils/referral';

type UnknownObject = Record<string, any>;

const isRecord = (value: unknown): value is UnknownObject =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const toNumber = (value: unknown, fallback = 0) => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
};

const toNullableNumber = (value: unknown) => {
  const parsed = toNumber(value, Number.NaN);
  return Number.isNaN(parsed) ? null : parsed;
};

const toBoolean = (value: unknown, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return fallback;
};

const pickFirstDefined = <T = unknown>(
  source: UnknownObject,
  keys: string[]
): T | undefined => {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key] as T;
    }
  }
  return undefined;
};

const extractSummaryContainer = (payload: unknown): UnknownObject => {
  if (!isRecord(payload)) return {};

  const nested =
    payload.data ??
    payload.summary ??
    payload.referral ??
    payload.referralSummary ??
    payload.payload;

  return isRecord(nested) ? nested : payload;
};

const extractLeaderboardCollection = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return [];

  const nested =
    payload.data ??
    payload.entries ??
    payload.content ??
    payload.results ??
    payload.leaderboard ??
    payload.payload;

  if (Array.isArray(nested)) return nested;
  if (isRecord(nested) && Array.isArray(nested.entries)) return nested.entries;
  if (isRecord(nested) && Array.isArray(nested.content)) return nested.content;

  return [];
};

export const normalizeReferralSummary = (
  payload: unknown,
  fallback: ReferralSummary
): ReferralSummary => {
  const source = extractSummaryContainer(payload);

  const referralCode =
    pickFirstDefined<string>(source, [
      'referralCode',
      'code',
      'refCode',
      'inviteCode',
    ]) ?? fallback.referralCode;

  const referralLink =
    pickFirstDefined<string>(source, ['referralLink', 'link', 'shareLink']) ??
    fallback.referralLink;

  const stats = isRecord(source.stats) ? source.stats : {};
  const counts = isRecord(source.counts) ? source.counts : {};

  return {
    isEligible: toBoolean(
      pickFirstDefined(source, [
        'isEligible',
        'isEligibleReferrer',
        'eligible',
      ]),
      fallback.isEligible
    ),
    referralCode,
    referralLink,
    points: toNumber(
      pickFirstDefined(source, [
        'points',
        'userPoints',
        'totalPoints',
        'qualifiedPoints',
      ]) ?? stats.points,
      fallback.points
    ),
    referralCount: toNumber(
      pickFirstDefined(source, [
        'referralCount',
        'totalReferrals',
        'count',
      ]) ?? stats.referralCount,
      fallback.referralCount
    ),
    pendingReferralCount: toNumber(
      pickFirstDefined(source, [
        'pendingReferralCount',
        'pendingCount',
        'pendingReferrals',
      ]) ??
        stats.pendingReferralCount ??
        counts.pending,
      fallback.pendingReferralCount
    ),
    qualifiedReferralCount: toNumber(
      pickFirstDefined(source, [
        'qualifiedReferralCount',
        'qualifiedCount',
        'qualifiedReferrals',
        'referralCount',
      ]) ??
        stats.qualifiedReferralCount ??
        counts.qualified,
      fallback.qualifiedReferralCount
    ),
    leaderboardRank: toNullableNumber(
      pickFirstDefined(source, [
        'leaderboardRank',
        'rank',
        'leaderboardPosition',
      ]) ?? stats.leaderboardRank
    ) ?? fallback.leaderboardRank,
  };
};

export const normalizeReferralLeaderboardEntries = (payload: unknown) => {
  return extractLeaderboardCollection(payload).map((entry, index) => {
    const source = isRecord(entry) ? entry : {};
    const user = isRecord(source.user) ? source.user : {};

    const email =
      pickFirstDefined<string>(source, ['email', 'userEmail']) ??
      pickFirstDefined<string>(user, ['email']);

    const emailValue = email ?? 'Unavailable';
    const maskedEmail =
      pickFirstDefined<string>(source, ['maskedEmail', 'emailMasked']) ??
      (emailValue.includes('*')
        ? emailValue
        : maskReferralEmail(emailValue));

    return {
      rank: toNumber(
        pickFirstDefined(source, ['rank', 'position', 'leaderboardRank']),
        index + 1
      ),
      email,
      maskedEmail,
      points: toNumber(
        pickFirstDefined(source, ['points', 'totalPoints', 'score']),
        0
      ),
      referralCount: toNumber(
        pickFirstDefined(source, [
          'referralCount',
          'qualifiedReferralCount',
          'count',
        ]),
        0
      ),
      avatarUrl:
        pickFirstDefined<string>(source, ['avatarUrl', 'profileImage']) ??
        pickFirstDefined<string>(user, ['profileImage', 'avatarUrl']) ??
        null,
    } satisfies ReferralLeaderboardEntry;
  });
};
